// Admin Layout with Navigation
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../utils/authContext';
import { useAdminTranslation } from '../utils/i18nContext';
import { Button } from "@/components/ui/button";
import LanguageToggle from '../components/LanguageToggle';
import {
  LayoutDashboard,
  Users,
  BarChart3,
  Download,
  Activity,
  LogOut,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, admin } = useAdminAuth();
  const { t } = useAdminTranslation();

  const navItems = [
    { path: '/neuro-admin-access/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { path: '/neuro-admin-access/users', label: t('users'), icon: Users },
    { path: '/neuro-admin-access/analytics', label: t('analytics'), icon: BarChart3 },
    { path: '/neuro-admin-access/export', label: t('export'), icon: Download },
    { path: '/neuro-admin-access/health', label: t('system_health'), icon: Activity }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/neuro-admin-access/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-cyan-400" />
            <h1 className="text-xl font-bold">{t('admin_panel')}</h1>
          </div>
          
          <div className="flex items-center gap-4">
            <LanguageToggle />
            <span className="text-sm text-gray-400">{admin?.email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-red-500/20 text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('logout')}
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar Navigation */}
          <aside className="w-64 flex-shrink-0">
            <nav className="space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
